import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicOptionComponent, SelectComponent } from '@mzic/mzic-components';
import { SelectOptionData } from '@mzic/mzic-interfaces';
import { catchError, map, Observable, of, Subject, switchMap } from 'rxjs';
import { StatementOriginRequiredInputConstraint } from '../../interfaces/collection-revision.interface';
import {
  CollectionRevisionScope,
  StatementOriginCatalogQuery,
  StatementOriginRow,
} from '../../interfaces/statement-origin.interface';
import { CollectionRevisionStatementOriginService } from '../../services/collection-revision-statement-origin.service';

/** Closed autocomplete: top page only, refine through the catalog search. */
export const STATEMENT_ORIGIN_PICKER_PAGE_SIZE = 50;

interface StatementOriginCatalogResult {
  readonly search: string | null;
  readonly rows: readonly StatementOriginRow[] | null;
}

/**
 * Single-select payor ("fonte pagadora") picker for the collections
 * required-input dialog. It composes `mzic-select` (searchable, closed,
 * no create affordance) over the revision-scoped statement-origin catalog
 * facade. Presentational: every identifier arrives through inputs.
 */
@Component({
  selector: 'lib-statement-origin-picker',
  imports: [
    CommonModule,
    TranslocoModule,
    SelectComponent,
    MzicOptionComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './statement-origin-picker.component.html',
  styleUrl: './statement-origin-picker.component.scss',
})
export class StatementOriginPickerComponent implements OnChanges {
  @Input({ required: true }) collectionId!: string;
  @Input({ required: true }) revisionId!: string;
  @Input({ required: true })
  constraint!: StatementOriginRequiredInputConstraint;
  @Input() selectedId: string | null = null;
  @Input() disabled = false;
  @Input() invalid = false;
  @Output() readonly selectionChange =
    new EventEmitter<StatementOriginRow | null>();

  rows: readonly StatementOriginRow[] = [];
  selectedOptions: SelectOptionData[] | undefined = undefined;
  searching = false;
  hydrating = false;
  loadFailed = false;

  private readonly statementOrigins = inject(
    CollectionRevisionStatementOriginService,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly catalogRequest$ = new Subject<string | null>();
  private readonly hydrationRequest$ = new Subject<string>();
  private selectedRow: StatementOriginRow | null = null;
  private catalogRequested = false;
  private catalogLoaded = false;
  private appliedSearch: string | null = null;
  private closingSelect = false;

  constructor() {
    this.catalogRequest$
      .pipe(
        switchMap((search) => this.loadCatalogPage(search)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.applyCatalogResult(result));

    this.hydrationRequest$
      .pipe(
        switchMap((statementOriginId) =>
          this.statementOrigins
            .get(this.revisionScope(), statementOriginId)
            .pipe(catchError(() => of(null))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((row) => {
        this.hydrating = false;
        this.applySelectedRow(row && row.id === this.selectedId ? row : null);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      hasChangedAfterInit(changes['collectionId']) ||
      hasChangedAfterInit(changes['revisionId']) ||
      hasChangedAfterInit(changes['constraint'])
    ) {
      this.resetCatalog();
    }
    if (changes['selectedId']) {
      this.syncSelectedRow();
    }
  }

  get showEmptyState(): boolean {
    return (
      this.catalogLoaded &&
      !this.searching &&
      !this.loadFailed &&
      this.rows.length === 0
    );
  }

  initialFor(row: StatementOriginRow): string {
    return row.display_name.trim()[0]?.toUpperCase() ?? '?';
  }

  isSelected(row: StatementOriginRow): boolean {
    return row.id === this.selectedId;
  }

  /**
   * The catalog is fetched lazily: the first interaction with the trigger
   * loads page 0 without a search filter. Later interactions are ignored
   * unless the previous attempt failed (retry on reopen).
   */
  onPickerOpenIntent(): void {
    // The trigger click that CLOSES the dropdown emits `closedChange`
    // synchronously and then keeps bubbling to this wrapper; that click is
    // a close intent, not an open intent, so it must not refetch.
    if (this.closingSelect) return;
    if (this.disabled || this.hydrating) return;
    if (this.catalogRequested && !this.loadFailed) return;
    this.requestCatalog(null);
  }

  onSearchTextChange(searchText: string): void {
    const search = searchText.trim() || null;
    if (
      this.catalogRequested &&
      !this.loadFailed &&
      this.appliedSearch === search
    ) {
      return;
    }
    this.requestCatalog(search);
  }

  onClosedChange(): void {
    // Suppress the open-intent handler for the remainder of the current
    // event dispatch only: a backdrop click or Escape never reaches the
    // wrapper, so the flag must clear on its own before the next real click.
    this.closingSelect = true;
    queueMicrotask(() => {
      this.closingSelect = false;
    });
    this.searching = false;
    if (this.appliedSearch !== null || this.loadFailed) {
      this.catalogRequested = false;
      this.catalogLoaded = false;
    }
  }

  onPick(row: StatementOriginRow): void {
    if (this.disabled) return;
    this.applySelectedRow(row);
    this.selectionChange.emit(row);
  }

  private requestCatalog(search: string | null): void {
    this.catalogRequested = true;
    this.loadFailed = false;
    this.searching = true;
    this.catalogRequest$.next(search);
  }

  private loadCatalogPage(
    search: string | null,
  ): Observable<StatementOriginCatalogResult> {
    const query: StatementOriginCatalogQuery = {
      constraint: this.constraint,
      page: 0,
      size: STATEMENT_ORIGIN_PICKER_PAGE_SIZE,
      ...(search === null ? {} : { search }),
    };
    return this.statementOrigins.list(this.revisionScope(), query).pipe(
      map(
        (page): StatementOriginCatalogResult => ({
          search,
          rows: page.items,
        }),
      ),
      catchError(() =>
        of<StatementOriginCatalogResult>({ search, rows: null }),
      ),
    );
  }

  private applyCatalogResult(result: StatementOriginCatalogResult): void {
    this.searching = false;
    if (result.rows === null) {
      this.loadFailed = true;
      this.catalogLoaded = false;
      this.rows = [];
      return;
    }
    this.loadFailed = false;
    this.catalogLoaded = true;
    this.appliedSearch = result.search;
    this.rows = result.rows;
  }

  private resetCatalog(): void {
    this.catalogRequested = false;
    this.catalogLoaded = false;
    this.loadFailed = false;
    this.searching = false;
    this.appliedSearch = null;
    this.rows = [];
  }

  private syncSelectedRow(): void {
    const selectedId = this.selectedId;
    if (!selectedId) {
      this.applySelectedRow(null);
      return;
    }
    if (this.selectedRow?.id === selectedId) return;
    const loaded = this.rows.find((row) => row.id === selectedId);
    if (loaded) {
      this.applySelectedRow(loaded);
      return;
    }
    this.hydrating = true;
    this.hydrationRequest$.next(selectedId);
  }

  /**
   * `SelectComponent` compares `optionsSelected` by content, so a new array
   * is produced only when the rendered selection actually changes.
   */
  private applySelectedRow(row: StatementOriginRow | null): void {
    if (rendersSameSelection(this.selectedRow, row)) return;
    this.selectedRow = row;
    this.selectedOptions = row
      ? [
          {
            id: row.id,
            title: row.display_name,
            description: row.description ?? undefined,
            image: row.icon_url ?? undefined,
          },
        ]
      : undefined;
  }

  private revisionScope(): CollectionRevisionScope {
    return { collectionId: this.collectionId, revisionId: this.revisionId };
  }
}

function hasChangedAfterInit(
  change: SimpleChanges[string] | undefined,
): boolean {
  return Boolean(change) && !change?.firstChange;
}

function rendersSameSelection(
  previous: StatementOriginRow | null,
  next: StatementOriginRow | null,
): boolean {
  if (previous === null || next === null) return previous === next;
  return (
    previous.id === next.id &&
    previous.display_name === next.display_name &&
    previous.description === next.description &&
    previous.icon_url === next.icon_url
  );
}
